type NodeConstructor = new (...args: unknown[]) => Node;

export const isClient =
    typeof window !== "undefined" &&
    typeof Document !== "undefined" &&
    typeof HTMLElement !== "undefined";

export function getDocument(node?: Node | null): Document | null {
    if (!node && typeof node !== "undefined") return null;
    if (node) {
        if (node.ownerDocument) {
            return node.ownerDocument;
        } else if (typeof node.getRootNode === "function") {
            const root = node.getRootNode();
            if (root && "defaultView" in (root as object)) {
                return root as Document;
            }
        }
    } else if (typeof document !== "undefined") {
        return document;
    }
    return null;
}

export function getWindow(node?: Node | null): Window | null {
    if (!node && typeof node !== "undefined") return null;
    if (node) {
        const document = getDocument(node);
        if (document) {
            return document.defaultView;
        }
    } else if (typeof window !== "undefined") {
        return window;
    } else if (typeof document !== "undefined") {
        return document.defaultView;
    }
    return null;
}

export function isDOMInstance<T extends Node>(
    node: unknown,
    type: NodeConstructor = Node
): node is T {
    if (node instanceof type) return true;
    const window = getWindow(node as Node);
    if (window) {
        type =
            (window as unknown as Record<string, NodeConstructor>)[type.name] ||
            type;
    }
    return node instanceof type;
}

export function findAncestor(
    node: HTMLElement | null,
    predicate: (node: HTMLElement) => boolean
): HTMLElement | null {
    while (node) {
        if (predicate(node)) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

/**
 * Get the nearest ancestor element that has overflow set to scroll.
 */
export function getNearestScrollNode(node: Node | null): HTMLElement | null {
    if (!isClient) return null;
    if (isDOMInstance<Document>(node, Document)) node = node.documentElement;
    if (!isDOMInstance<HTMLElement>(node, HTMLElement)) return null;
    return findAncestor(node, (node) => {
        const { overflowX, overflowY } = getComputedStyle(node);
        if (overflowX === "scroll") return true;
        if (overflowY === "scroll") return true;
        return false;
    });
}
